import { Component, inject, input, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { PluginCardData } from "@bitwarden/web-vault/app/components/plugin-card/plugin-card.component";
import { DEFAULT_PLUGIN_LOGO } from "@bitwarden/web-vault/app/models/data/plugin-metadata";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import {
  ConfirmationDialogService,
  CtaNotificationData,
} from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import {
  CHATBOT_PLUGIN_NAME,
  EODHD_PLUGIN_URL,
  FISKIL_PLUGIN_NAME,
  GROCERY_PLUGIN_NAME,
  PLAID_PLUGIN_NAME,
  PluginConfigService,
  PluginIdentifier,
  RECURRING_DETECTOR_PLUGIN_ID,
  WEALTH_COMPARISON_PLUGIN_NAME,
  TRANSFER_CONVERSION_PLUGIN_NAME,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { isToggleOnlyPluginId } from "@bitwarden/web-vault/app/services/store/data-plugin/toggle-only-plugins";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { Origin, SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { GlossSync } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { FiskilConsentService } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil-consent.service";
import {
  PlaidConsentService,
  PlaidDataPurgeFailedError,
  PlaidPartialRemovalError,
} from "@bitwarden/web-vault/app/services/syncing/plaid/plaid-consent.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { GlossExtensionService } from "@bitwarden/web-vault/app/services/extension/gloss-extension.service";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { getAccountStatusView } from "@bitwarden/web-vault/app/components/tables/account-tables/account.table";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";

@Component({
  selector: "app-plugin-details-dialog",
  templateUrl: "./plugin-details-dialog.component.html",
})
export class PluginDetailsDialogComponent implements OnInit {
  plugin = input.required<PluginCardData>();
  closeDialog = input<CallableFunction | undefined>(undefined);
  onInstallSuccess = input<CallableFunction | undefined>(undefined);
  isInstalled = input<boolean>(false);
  parentDialogFactory = input<DialogFactory | undefined>(undefined);
  /** When true (and not yet installed), immediately continue into the connect flow on load
   * instead of waiting for an explicit "Enable" click — see issue #5076. */
  autoOpenForm = input<boolean>(false);

  readonly defaultPluginLogo = DEFAULT_PLUGIN_LOGO;
  readonly chatbotPluginId = CHATBOT_PLUGIN_NAME;
  readonly fiskilPluginId = FISKIL_PLUGIN_NAME;
  readonly plaidPluginId = PLAID_PLUGIN_NAME;
  readonly eodhdPluginId = PluginIdentifier.DATABANK;
  readonly recurringDetectorPluginId = RECURRING_DETECTOR_PLUGIN_ID;
  readonly groceryPluginId = GROCERY_PLUGIN_NAME;
  readonly wealthComparisonPluginId = WEALTH_COMPARISON_PLUGIN_NAME;
  readonly transactionPluginId = PluginIdentifier.TRANSACTION;
  readonly glossExtensionPluginId = GlossExtensionService.PLUGIN_ID;
  readonly transferConversionPluginId = TRANSFER_CONVERSION_PLUGIN_NAME;

  apiKeyForm!: FormGroup;
  protected isSubmitting = signal(false);

  private router = inject(Router);
  private dialogFactory = new DialogFactory();
  private confirmationDialog = inject(ConfirmationDialogService);
  private extensionService = inject(GlossExtensionService);
  private userStore = inject(UserStoreService);
  private importStore = inject(ImportStoreService);
  private fiskilConsentService = inject(FiskilConsentService);
  private plaidConsentService = inject(PlaidConsentService);
  private glossSync = inject(GlossSync);
  private i18nService = inject(I18nService);
  private log = inject(LogService);
  private pluginConfigService = inject(PluginConfigService);
  private dataPluginStore = inject(DataPluginStoreService);
  private fb = inject(FormBuilder);
  private roleAccessService = inject(RoleAccessService);

  ngOnInit(): void {
    this.apiKeyForm = this.fb.group({
      apiKey: ["", Validators.required],
    });

    if (this.plugin()?.id === PluginIdentifier.DATABANK && this.isInstalled()) {
      const config = this.pluginConfigService.getConfig(PluginIdentifier.DATABANK);
      if (config?.apiKey) {
        this.apiKeyForm.patchValue({ apiKey: config.apiKey });
      }
    }

    if (this.autoOpenForm() && !this.isInstalled() && this.hasGenericConnectForm) {
      void this.onInstall();
    }
  }

  /** True for plugins whose "connect" action opens the generic credential form
   * (PluginConfigComponent) rather than an OAuth confirmation, embedded form, or
   * one-click toggle — those have their own dedicated flows and shouldn't be
   * auto-triggered just from viewing the details page. */
  private get hasGenericConnectForm(): boolean {
    const pluginId = this.plugin()?.id;
    if (!pluginId || isToggleOnlyPluginId(pluginId)) {
      return false;
    }
    return ![
      this.fiskilPluginId,
      this.plaidPluginId,
      this.eodhdPluginId,
      this.recurringDetectorPluginId,
      this.glossExtensionPluginId,
      this.wealthComparisonPluginId,
      this.chatbotPluginId,
      // One-click enable (like chatbot): its onInstall commits immediately, so it must
      // not be auto-fired just from landing on the details page (store card / search
      // deep link) - the user has to click Enable explicitly.
      this.transferConversionPluginId,
      // Grocery is also one-click (installGroceryPlugin commits a vault row immediately),
      // so opening its details from the grocery page's prompt must show the Enable button
      // for the user to click — never auto-install.
      this.groceryPluginId,
    ].includes(pluginId);
  }

  openRecurringDetector(): void {
    this.closeDialog()?.();
    void this.router.navigate(["/", GlossRoutes.RecurringDetector]);
  }

  private openGrocery(): void {
    this.closeDialog()?.();
    void this.router.navigate(["/", GlossRoutes.GlossGrocery]);
  }

  async onInstall(): Promise<void> {
    const pluginId = this.plugin()?.id;
    if (!pluginId) {
      return;
    }

    if (pluginId === FISKIL_PLUGIN_NAME) {
      await this.installFiskilPlugin();
      return;
    }

    if (pluginId === PLAID_PLUGIN_NAME) {
      await this.installPlaidPlugin();
      return;
    }

    if (pluginId === PluginIdentifier.DATABANK) {
      await this.saveEodhdPlugin();
      return;
    }

    if (pluginId === RECURRING_DETECTOR_PLUGIN_ID) {
      await this.installRecurringDetectorPlugin();
      return;
    }

    if (pluginId === GROCERY_PLUGIN_NAME) {
      await this.installGroceryPlugin();
      return;
    }

    if (pluginId === this.glossExtensionPluginId) {
      await this.installGlossExtension();
      return;
    }

    if (pluginId === TRANSFER_CONVERSION_PLUGIN_NAME) {
      await this.setTransferConversionEnabled(true);
      return;
    }

    if (pluginId === this.wealthComparisonPluginId) {
      this.isSubmitting.set(true);
      try {
        const activated = await this.pluginConfigService.ensureWealthComparisonPluginActivated();
        if (!activated) {
          this.confirmationDialog.notify("error", "error", "somethingWentWrong");
          return;
        }
        this.onInstallSuccess()?.();
        const close = this.closeDialog();
        if (close) {
          close();
        }
        const factory = this.parentDialogFactory() || this.dialogFactory;
        await factory.openWealthComparisonDialog();
      } catch (error) {
        this.log.error("Failed to install wealth comparison plugin: " + JSON.stringify(error));
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      } finally {
        this.isSubmitting.set(false);
      }
      return;
    }

    if (isToggleOnlyPluginId(pluginId)) {
      this.pluginConfigService.setToggleOnlyPluginEnabled(pluginId, true);
      this.confirmationDialog.notify("success", "success", "pluginInstalledSuccessfully");
      this.onInstallSuccess()?.();
      this.closeDialog()?.();
      return;
    }

    if (pluginId === CHATBOT_PLUGIN_NAME) {
      this.pluginConfigService.setChatbotPluginEnabled(true);
      this.confirmationDialog.notify("success", "success", "pluginInstalledSuccessfully");
      this.onInstallSuccess()?.();
      this.closeDialog()?.();
      return;
    }

    const close = this.closeDialog();
    if (close) {
      close();
    }

    const factory = this.parentDialogFactory() || this.dialogFactory;
    factory.openPluginConfigDialog(pluginId);
  }

  private async saveEodhdPlugin(isUpdate = false): Promise<void> {
    if (!this.apiKeyForm.valid) {
      this.apiKeyForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    try {
      const { apiKey } = this.apiKeyForm.value;
      const saved = await this.pluginConfigService.upsertConfig(
        { url: EODHD_PLUGIN_URL, name: PluginIdentifier.DATABANK },
        { apiKey },
      );

      if (!saved) {
        this.confirmationDialog.notify("error", "error", "eodhdPluginSaveFailed");
        return;
      }

      // The databank collection keeps its own snapshot of this config and an auth token for its
      // key, refreshed only by triggerChanges() (otherwise at startup). Without this, Find Price
      // is offered (it checks the live config) but searches the stale one until a reload: no
      // results after an install, the old key after an update.
      this.dataPluginStore.databank.triggerChanges();

      const successKey = isUpdate ? "eodhdPluginUpdated" : "eodhdPluginInstalled";
      this.confirmationDialog.notify("success", "success", successKey);
      this.onInstallSuccess()?.();

      const close = this.closeDialog();
      if (close) {
        close();
      }
    } catch (error) {
      this.log.error("Failed to save EODHD plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "eodhdPluginSaveFailed");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async installFiskilPlugin(): Promise<void> {
    const close = this.closeDialog();
    if (close) {
      close();
    }

    const data: CtaNotificationData = {
      title: this.i18nService.t("fiskilPluginInstallTitle"),
      message: this.i18nService.t("fiskilPluginInstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.glossSync.connect(Origin.fiskil),
        title: this.i18nService.t("fiskilPluginInstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  private async installPlaidPlugin(): Promise<void> {
    const close = this.closeDialog();
    if (close) {
      close();
    }

    const data: CtaNotificationData = {
      title: this.i18nService.t("plaidPluginInstallTitle"),
      message: this.i18nService.t("plaidPluginInstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.glossSync.connect(Origin.plaid),
        title: this.i18nService.t("plaidPluginInstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  /**
   * "Install" flow for the browser extension. A web page can't install a
   * Chrome extension for the user, so this opens the extensions page (best
   * effort — browsers may block navigating to `chrome://` from web content)
   * and shows the load-unpacked steps. The confirm button re-pings; the app
   * also auto-detects the extension on the next page load, which flips the
   * grocery gate and this card's badge to "installed".
   */
  private async installGlossExtension(): Promise<void> {
    const close = this.closeDialog();
    if (close) {
      close();
    }

    // Best-effort: some browsers open a blank tab instead — the steps below
    // are the reliable path.
    try {
      window.open("chrome://extensions/", "_blank");
    } catch {
      // ignore — the instructions cover it
    }

    const data: CtaNotificationData = {
      title: this.i18nService.t("glossExtensionInstallTitle"),
      message: this.i18nService.t("glossExtensionInstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.recheckGlossExtension(),
        title: this.i18nService.t("glossExtensionInstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  /** Re-pings the extension after the user says they've loaded it. */
  private async recheckGlossExtension(): Promise<void> {
    const installed = await this.extensionService.check();
    if (installed) {
      this.confirmationDialog.notify("success", "success", "glossExtensionDetected");
      this.onInstallSuccess()?.();
    } else {
      this.confirmationDialog.notify(
        "info",
        "glossExtensionNotDetectedTitle",
        "glossExtensionNotDetected",
      );
    }
  }

  /**
   * Enable or disable the Transfers & Conversions feature by writing the
   * `enabled` flag to its plugin options. Disabling preserves the row (and the
   * user's manual overrides) rather than deleting it.
   */
  private async setTransferConversionEnabled(enabled: boolean): Promise<void> {
    this.isSubmitting.set(true);
    try {
      const saved = await this.pluginConfigService.saveTransferConversionOptions({ enabled });
      if (!saved) {
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
        return;
      }
      this.confirmationDialog.notify(
        "success",
        "success",
        enabled ? "pluginInstalledSuccessfully" : "pluginUninstalledSuccessfully",
      );
      this.onInstallSuccess()?.();
      this.closeDialog()?.();
    } catch (error) {
      this.log.error("Failed to update Transfers & Conversions plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async installRecurringDetectorPlugin(): Promise<void> {
    this.isSubmitting.set(true);
    try {
      // apiKey is a required field by upsertConfig; the plugin ID is used as a sentinel
      // because this is a local plugin with no external API credential.
      const saved = await this.pluginConfigService.upsertConfig(
        { url: "", name: RECURRING_DETECTOR_PLUGIN_ID },
        { apiKey: RECURRING_DETECTOR_PLUGIN_ID },
      );
      if (!saved) {
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
        return;
      }
      this.confirmationDialog.notify("success", "success", "recurringDetectorInstalled");
      this.onInstallSuccess()?.();
      // It has no main nav entry (only the More overview lists it), so take the user straight to
      // the feature, as Open does (#6047).
      this.openRecurringDetector();
    } catch (error) {
      this.log.error("Failed to install Recurring Detector: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async installGroceryPlugin(): Promise<void> {
    this.isSubmitting.set(true);
    try {
      // Grocery is a local, routed feature with no external API credential — mirror the
      // Recurring Detector pattern: persist a vault plugin row keyed by a sentinel apiKey.
      // The presence of that row is what reveals the grocery page's content.
      const saved = await this.pluginConfigService.upsertConfig(
        { url: "", name: GROCERY_PLUGIN_NAME },
        { apiKey: GROCERY_PLUGIN_NAME },
      );
      if (!saved) {
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
        return;
      }
      this.confirmationDialog.notify("success", "success", "groceryPluginInstalled");
      this.onInstallSuccess()?.();
      // Grocery has no nav entry (#5818), and users often arrive from the grocery page's
      // "plugin required" prompt, so take them straight to the feature, as onUpdate's Open does.
      this.openGrocery();
    } catch (error) {
      this.log.error("Failed to install Grocery plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async onUninstall(): Promise<void> {
    const pluginId = this.plugin()?.id;
    if (!pluginId) {
      return;
    }

    if (pluginId === FISKIL_PLUGIN_NAME) {
      await this.confirmUninstallFiskilPlugin();
      return;
    }

    if (pluginId === PLAID_PLUGIN_NAME) {
      await this.confirmUninstallPlaidPlugin();
      return;
    }

    if (pluginId === TRANSFER_CONVERSION_PLUGIN_NAME) {
      // Disable, don't delete: keeps the user's manual links/exclusions
      // (lTid/xLid) intact so re-enabling restores them.
      await this.setTransferConversionEnabled(false);
      return;
    }

    this.isSubmitting.set(true);
    try {
      if (isToggleOnlyPluginId(pluginId)) {
        this.pluginConfigService.setToggleOnlyPluginEnabled(pluginId, false);
        this.confirmationDialog.notify("success", "success", "pluginUninstalledSuccessfully");
        this.onInstallSuccess()?.();
        this.closeDialog()?.();
        return;
      }

      if (pluginId === CHATBOT_PLUGIN_NAME) {
        this.pluginConfigService.setChatbotPluginEnabled(false);
        this.confirmationDialog.notify("success", "success", "pluginUninstalledSuccessfully");
        this.onInstallSuccess()?.();
        this.closeDialog()?.();
        return;
      }

      if (pluginId === this.wealthComparisonPluginId) {
        await this.confirmUninstallWealthComparisonPlugin();
        return;
      }

      const pluginToDelete = this.userStore.plugin.collection()?.find((p) => p.name === pluginId);
      if (!pluginToDelete) {
        this.confirmationDialog.notify("error", "error", "pluginNotFound");
        return;
      }

      if (pluginToDelete.keyId) {
        const keyToDelete = this.userStore.key
          .collection()
          ?.find((k) => k.id === pluginToDelete.keyId);
        if (keyToDelete) {
          await this.userStore.key.deleteFromVault([keyToDelete]);
        }
      }

      await this.userStore.plugin.deleteFromVault([pluginToDelete]);
      if (pluginId === PluginIdentifier.DATABANK) {
        // Drop the databank collection's snapshot and the removed key's token too (see
        // saveEodhdPlugin).
        this.dataPluginStore.databank.triggerChanges();
      }
      this.confirmationDialog.notify("success", "success", "pluginUninstalledSuccessfully");

      this.onInstallSuccess()?.();
      const close = this.closeDialog();
      if (close) {
        close();
      }
    } catch (error) {
      this.log.error("Failed to uninstall plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async confirmUninstallFiskilPlugin(): Promise<void> {
    const data: CtaNotificationData = {
      title: this.i18nService.t("fiskilPluginUninstallTitle"),
      message: this.i18nService.t("fiskilPluginUninstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.executeUninstallFiskilPlugin(),
        title: this.i18nService.t("fiskilPluginUninstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  private async executeUninstallFiskilPlugin(): Promise<void> {
    this.isSubmitting.set(true);
    try {
      await this.fiskilConsentService.removeConsentAndData();
      this.confirmationDialog.notify("success", "success", "fiskilPluginDisabledNow");
      this.onInstallSuccess()?.();
      const close = this.closeDialog();
      if (close) {
        close();
      }
    } catch (error) {
      this.log.error("Failed to uninstall Fiskil plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async confirmUninstallPlaidPlugin(): Promise<void> {
    const data: CtaNotificationData = {
      title: this.i18nService.t("plaidPluginUninstallTitle"),
      message: this.i18nService.t("plaidPluginUninstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.executeUninstallPlaidPlugin(),
        title: this.i18nService.t("plaidPluginUninstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  private async executeUninstallPlaidPlugin(): Promise<void> {
    // Mirrors executeUninstallFiskilPlugin: do the async work first, then notify/refresh/close on
    // success. If the await is moved after close(), a failure toast fires with the dialog already
    // gone — the user has no context to retry from, and onInstallSuccess fires before we know if
    // the uninstall actually succeeded.
    this.isSubmitting.set(true);
    try {
      await this.plaidConsentService.removeConsentAndData();
      this.confirmationDialog.notify("success", "success", "plaidUninstalledSuccessfully");
      this.onInstallSuccess()?.();
      const close = this.closeDialog();
      if (close) {
        close();
      }
    } catch (error) {
      if (
        !(error instanceof PlaidPartialRemovalError) &&
        !(error instanceof PlaidDataPurgeFailedError)
      ) {
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      }
      this.log.error("Failed to uninstall Plaid plugin: " + JSON.stringify(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async confirmUninstallWealthComparisonPlugin(): Promise<void> {
    const data: CtaNotificationData = {
      title: this.i18nService.t("wealthComparisonUninstallTitle"),
      message: this.i18nService.t("wealthComparisonUninstallMessage"),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.executeUninstallWealthComparisonPlugin(),
        title: this.i18nService.t("wealthComparisonUninstallConfirm"),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  private async executeUninstallWealthComparisonPlugin(): Promise<void> {
    this.onInstallSuccess()?.();
    const close = this.closeDialog();
    if (close) {
      close();
    }
    try {
      const deleted = await this.pluginConfigService.deletePluginByName(
        this.wealthComparisonPluginId,
      );
      if (!deleted) {
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
        return;
      }
      this.confirmationDialog.notify("success", "success", "pluginUninstalledSuccessfully");
    } catch (error) {
      this.log.error("Failed to uninstall wealth comparison plugin: " + JSON.stringify(error));
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  get developerName(): string {
    const author = this.plugin()?.author;
    return author?.replace(/^By\s+/i, "");
  }

  /** Clickable mailto contact link is a beta-only enhancement; other users see plain text. */
  get isBetaUser(): boolean {
    return this.roleAccessService.isBetaUserSignal();
  }

  /** "Tool · Finance · GLOSS Vault" line under the title in the details view. */
  get detailsSubtitle(): string {
    const id = this.plugin()?.id;
    const isTool = this.plugin()?.type === "Tools" || id === this.recurringDetectorPluginId;
    return [
      isTool ? "Tool" : "Plugin",
      this.plugin()?.type === "Tools" ? undefined : this.plugin()?.type,
      this.developerName,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  /** Two-letter monogram for the logo tile (the first letters of the first two words, else the first two). */
  get monogram(): string {
    const words = (this.plugin()?.title ?? "")
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .split(/\s+/)
      .filter(Boolean);
    // Array.from splits by code point, so letters outside the BMP aren't cut in half.
    const letters =
      words.length > 1
        ? [Array.from(words[0])[0], Array.from(words[1])[0]]
        : Array.from(words[0] ?? "").slice(0, 2);
    return letters.join("").toUpperCase();
  }

  /**
   * "Save for later" in the details footer (shows under Saved in Add tools & plugins).
   * Flips `isLiked` on the plugin object it was given, like the plugin card's like toggle, so the
   * caller must pass its own copy and never a shared PLUGIN_CATALOG entry (see PluginsComponent.plugins).
   */
  toggleSaved(): void {
    const plugin = this.plugin();
    if (plugin) {
      plugin.isLiked = !plugin.isLiked;
    }
  }

  /** Same "Tool/Plugin · Finance · GLOSS Vault" line as the details view, so both views agree. */
  get manageSubtitle(): string {
    return this.detailsSubtitle;
  }

  /** Plugins whose Disable/Disconnect deletes the saved vault connection (no dedicated confirmation of their own). */
  private get deletesVaultConnection(): boolean {
    const id = this.plugin()?.id;
    return (
      id !== this.fiskilPluginId &&
      id !== this.plaidPluginId &&
      id !== this.wealthComparisonPluginId &&
      id !== this.chatbotPluginId &&
      id !== this.transferConversionPluginId &&
      !isToggleOnlyPluginId(id)
    );
  }

  /**
   * Manage view's Enabled switch / Disconnect button. Same as onUninstall, but plugins that would
   * silently delete their saved connection ask first (the others confirm in onUninstall or only toggle).
   */
  async onDisable(): Promise<void> {
    if (!this.deletesVaultConnection) {
      await this.onUninstall();
      return;
    }
    const title = this.plugin()?.title;
    const { titleKey, messageKey, confirmKey } = this.disableConfirmKeys;
    const data: CtaNotificationData = {
      title: this.i18nService.t(titleKey, title),
      message: this.i18nService.t(messageKey, title),
      cancel: { method: () => {}, title: this.i18nService.t("cancel") },
      confirm: {
        method: () => this.onUninstall(),
        title: this.i18nService.t(confirmKey),
      },
      reminder: null,
    };
    await this.confirmationDialog.ctaNotification(data);
  }

  /**
   * Copy for the Manage view's disable confirm. Feature plugins with no connection (Grocery,
   * Recurring Detector) just turn off; data sources also say the imported data stays (Fiskil and
   * Plaid never get here: their uninstall confirms describe the data they delete).
   */
  private get disableConfirmKeys(): { titleKey: string; messageKey: string; confirmKey: string } {
    if (this.opensFeature) {
      return {
        titleKey: "pluginManageTurnOffConfirmTitle",
        messageKey: "pluginManageTurnOffConfirmMessage",
        confirmKey: "pluginManageTurnOffConfirm",
      };
    }
    return {
      titleKey: "pluginManageDisableConfirmTitle",
      messageKey: this.isDataSource
        ? "pluginManageDisableConfirmDataMessage"
        : "pluginManageDisableConfirmMessage",
      confirmKey: "pluginManageDisableConfirm",
    };
  }

  /** Plugins that are a plain on/off switch (no account to disconnect). */
  get isSwitchPlugin(): boolean {
    const id = this.plugin()?.id;
    return (
      id === this.chatbotPluginId ||
      id === this.transferConversionPluginId ||
      isToggleOnlyPluginId(id)
    );
  }

  /** Plugins that sync data from an outside account: removing them "disconnects" it. */
  get isDataSource(): boolean {
    const id = this.plugin()?.id;
    return ![
      this.recurringDetectorPluginId,
      this.groceryPluginId,
      this.wealthComparisonPluginId,
      this.glossExtensionPluginId,
      this.eodhdPluginId,
    ].includes(id);
  }

  /**
   * Plugins whose Manage view has a Sync card: data sources whose accounts the app can identify
   * (Fiskil and Plaid), so the card never shows only placeholders.
   */
  get showSync(): boolean {
    return this.isDataSource && !this.isSwitchPlugin && this.pluginAccounts !== undefined;
  }

  /** The vault accounts this plugin created, where the app can tell (Fiskil and Plaid only). */
  private get pluginAccounts(): AccountView[] | undefined {
    const origin = { [this.fiskilPluginId]: Origin.fiskil, [this.plaidPluginId]: Origin.plaid }[
      this.plugin()?.id
    ];
    if (!origin) {
      return undefined;
    }
    return (this.userStore.enrichAccounts.collection() ?? []).filter(
      (acc) => acc.origin === origin,
    );
  }

  get syncedAccountsLabel(): string {
    const accounts = this.pluginAccounts;
    return accounts === undefined ? "—" : String(accounts.length);
  }

  get lastSyncedLabel(): string {
    const accounts = this.pluginAccounts ?? [];
    const ids = new Set(accounts.map((acc) => acc.id));
    const views = this.importStore.syncStatus.syncStatusViews() ?? [];
    const latest = views
      .filter((s) => ids.has(s.accountId))
      .sort((a, b) => b.dateModified.getTime() - a.dateModified.getTime())[0];
    if (!latest) {
      return "—";
    }
    const when = latest.dateModified.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
    // Every sync writes a status row per account, and each account's result lands as it finishes,
    // so the newest row can be a success while another account's current status is a failure.
    // Check each account's current status, the same way the "needs attention" count does; any
    // state other than synced/syncing is an error in the app's own sync states.
    const failed = accounts.some((acc) => {
      // getAccountStatusView sorts the array it is given, so hand it a copy.
      const key = getAccountStatusView(acc, [...views])?.key;
      return key && key !== SyncStatusEnum.synced && key !== SyncStatusEnum.syncing;
    });
    if (failed) {
      return `${when} · ${this.i18nService.t("failed")}`;
    }
    if (latest.key === SyncStatusEnum.syncing) {
      return `${when} · ${this.i18nService.t("syncing")}`;
    }
    return when;
  }

  /** The credentials form can be shown inline (a saved connector config exists for the plugin). */
  get canEmbedConfig(): boolean {
    const id = this.plugin()?.id;
    return this.hasCredentials && id !== this.eodhdPluginId;
  }

  /** Feature plugins whose Manage view leads with an "Open" button. */
  get opensFeature(): boolean {
    const id = this.plugin()?.id;
    return (
      id === this.recurringDetectorPluginId ||
      id === this.wealthComparisonPluginId ||
      id === this.groceryPluginId
    );
  }

  /**
   * Plugins with saved credentials to re-enter ("Sign in again" card). Same set that gets the
   * "Fetch data" button, which re-opens the connect form with the saved credentials.
   */
  get hasCredentials(): boolean {
    const id = this.plugin()?.id;
    return (
      !this.opensFeature &&
      id !== this.fiskilPluginId &&
      id !== this.plaidPluginId &&
      id !== this.chatbotPluginId &&
      id !== this.transferConversionPluginId &&
      id !== this.glossExtensionPluginId &&
      !isToggleOnlyPluginId(id)
    );
  }

  async onUpdate(): Promise<void> {
    if (this.plugin()?.id === FISKIL_PLUGIN_NAME || isToggleOnlyPluginId(this.plugin()?.id)) {
      return;
    }

    if (this.plugin()?.id === RECURRING_DETECTOR_PLUGIN_ID) {
      return;
    }

    if (this.plugin()?.id === TRANSFER_CONVERSION_PLUGIN_NAME) {
      return;
    }

    if (this.plugin()?.id === GROCERY_PLUGIN_NAME) {
      // Grocery has no credentials to re-enter — the action button just opens the feature.
      this.openGrocery();
      return;
    }

    if (this.plugin()?.id === PluginIdentifier.DATABANK) {
      await this.saveEodhdPlugin(true);
      return;
    }

    if (this.plugin()?.id === this.wealthComparisonPluginId) {
      this.isSubmitting.set(true);
      try {
        const activated = await this.pluginConfigService.ensureWealthComparisonPluginActivated();
        if (!activated) {
          this.confirmationDialog.notify("error", "error", "somethingWentWrong");
          return;
        }
        const close = this.closeDialog();
        if (close) {
          close();
        }
        const factory = this.parentDialogFactory() || this.dialogFactory;
        await factory.openWealthComparisonDialog();
      } catch (error) {
        this.log.error("Failed to open wealth comparison plugin: " + JSON.stringify(error));
        this.confirmationDialog.notify("error", "error", "somethingWentWrong");
      } finally {
        this.isSubmitting.set(false);
      }
      return;
    }

    const close = this.closeDialog();
    if (close) {
      close();
    }

    const pluginId = this.plugin()?.id;
    if (pluginId) {
      const factory = this.parentDialogFactory() || this.dialogFactory;
      factory.openPluginConfigDialog(pluginId);
    }
  }

  openPluginConfig(): void {
    const close = this.closeDialog();
    if (close) {
      close();
    }

    const pluginId = this.plugin()?.id;
    if (pluginId) {
      const factory = this.parentDialogFactory() || this.dialogFactory;
      factory.openPluginConfigDialog(pluginId, { forceReauth: true });
    }
  }
}
